import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SocketProvider } from './context/SocketContext';
import { adminAPI } from './api';
import Landing from './pages/Landing';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Register from './pages/Register';
import LegalPage from './pages/LegalPage';
import SeoGuidePage from './pages/SeoGuidePage';
import Dashboard from './pages/Dashboard';
import NewOrder from './pages/NewOrder';
import Orders from './pages/Orders';
import Services from './pages/Services';
import Deposit from './pages/Deposit';
import DepositCheckout from './pages/DepositCheckout';
import ReferralProgram from './pages/ReferralProgram';
import VirtualNumbers from './pages/VirtualNumbers';
import Settings from './pages/Settings';
import Support from './pages/Support';
import Guide from './pages/Guide';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminOrders from './pages/AdminOrders';
import AdminSupport from './pages/AdminSupport';
import AdminServices from './pages/AdminServices';
import AdminServiceTesting from './pages/AdminServiceTesting';
import AdminSettings from './pages/AdminSettings';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import WhatsAppButton from './components/WhatsAppButton';
import { ADMIN_PERMISSION_DEFAULTS, canAccessAdminArea, isAdminRole } from './utils/adminPermissions';

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdminRole(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (user) return <Navigate to={isAdminRole(user.role) ? '/admin' : '/dashboard'} replace />;
  return children;
}

function MobileGoogleCallback() {
  useEffect(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    window.location.replace('nicebooster://auth/google' + search + hash);
  }, []);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', background: '#0b1120', color: '#fff' }}>
      <div className="spinner" />
      <p style={{ marginTop: 16, fontFamily: 'sans-serif' }}>Redirecting back to NiceBooster app...</p>
    </div>
  );
}

function LandingRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (user) return <Navigate to={isAdminRole(user.role) ? '/admin' : '/dashboard'} replace />;
  return children;
}

function AdminAreaRoute({ area, children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    adminAPI.settings()
      .then((response) => setSettings({ ...ADMIN_PERMISSION_DEFAULTS, ...response.data.settings }))
      .catch(() => setSettings(ADMIN_PERMISSION_DEFAULTS));
  }, []);

  if (!settings) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!canAccessAdminArea(user, settings, area)) return <Navigate to="/admin" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SocketProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/api/auth/google/callback" element={<MobileGoogleCallback />} />
              <Route path="/auth/google/callback" element={<MobileGoogleCallback />} />
              <Route path="/" element={<LandingRoute><Landing /></LandingRoute>} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="/privacy-policy" element={<PublicRoute><LegalPage type="privacy" /></PublicRoute>} />
              <Route path="/terms-of-service" element={<PublicRoute><LegalPage type="terms" /></PublicRoute>} />
              <Route path="/refund-policy" element={<PublicRoute><LegalPage type="refund" /></PublicRoute>} />
              <Route path="/guides/instagram-followers" element={<PublicRoute><SeoGuidePage slug="instagram-followers" /></PublicRoute>} />
              <Route path="/guides/tiktok-views" element={<PublicRoute><SeoGuidePage slug="tiktok-views" /></PublicRoute>} />
              <Route path="/guides/telegram-members" element={<PublicRoute><SeoGuidePage slug="telegram-members" /></PublicRoute>} />
              <Route path="/guides/website-traffic-explained" element={<PublicRoute><SeoGuidePage slug="website-traffic-explained" /></PublicRoute>} />

            {/* User routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="orders/new" element={<NewOrder />} />
              <Route path="services" element={<Services />} />
              <Route path="orders" element={<Orders />} />
              <Route path="deposit" element={<Deposit />} />
              <Route path="deposit/checkout" element={<DepositCheckout />} />
              <Route path="referrals" element={<ReferralProgram />} />
              <Route path="virtual-numbers" element={<VirtualNumbers />} />
              <Route path="settings" element={<Settings />} />
              <Route path="support" element={<Support />} />
              <Route path="guide" element={<Guide />} />
            </Route>

            {/* Admin routes */}
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminAreaRoute area="users"><AdminUsers /></AdminAreaRoute>} />
              <Route path="orders" element={<AdminAreaRoute area="orders"><AdminOrders /></AdminAreaRoute>} />
              <Route path="support" element={<AdminAreaRoute area="support"><AdminSupport /></AdminAreaRoute>} />
              <Route path="services" element={<AdminAreaRoute area="services"><AdminServices /></AdminAreaRoute>} />
              <Route path="service-testing" element={<AdminAreaRoute area="service_testing"><AdminServiceTesting /></AdminAreaRoute>} />
              <Route path="settings" element={<AdminAreaRoute area="settings"><AdminSettings /></AdminAreaRoute>} />
            </Route>
          </Routes>
          <WhatsAppButton />
        </BrowserRouter>
        </SocketProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
